/**
 * First-party social-click beacon.
 *
 * Records an inbound social visit — only `{ campaign, platform }` — into
 * `social_attribution` via `POST /api/social/track`. This is the click signal
 * the social learning-rollup ranks content by, and the conversion-measurement
 * layer for the whole social channel.
 *
 * CONSENT: this rides "necessary" consent, NOT opt-in "analytics". It stores
 * the originating post id + platform on our own domain, plus — when the caller
 * has one — the visitor's pseudonymous PostHog distinct id (`anonId`). That id
 * is the same first-party identifier the "necessary"-basis `$pageview` already
 * carries; no cross-site cookie, no PII. It is what lets a click row join the
 * later `signup` row (PostHog aliases the distinct id to the user on
 * identify): before 2026-10-20 every social click had a null `anon_id`, so
 * click → signup could only be credited through the first-touch cookie.
 * The caller (`posthog-provider`) invokes it BEFORE the analytics-consent gate
 * on purpose: previously it sat behind analytics consent, so cold social
 * visitors (who rarely accept the banner) never registered a click and
 * `social_attribution` stayed at ~1 row forever while posts drew real views.
 * The third-party PostHog `$pageview` stays correctly behind analytics consent.
 *
 * Fires once per campaign per browser session (sessionStorage dedupe); never
 * throws. Accepts the two campaign prefixes `/api/social/track` does: `sp_` (a
 * social post) and `lt_` (the linktree bio hub).
 */

/** True when this attribution is a trackable inbound social click. */
export function isTrackableSocialClick(input: {
	source?: string;
	campaign?: string;
}): boolean {
	const { source, campaign } = input;
	return (
		source === "social" &&
		!!campaign &&
		(campaign.startsWith("sp_") || campaign.startsWith("lt_"))
	);
}

/** Upper bound on an `anonId` we send; anything longer is not a PostHog id. */
const MAX_ANON_ID_LENGTH = 128;

export function recordSocialClick(input: {
	source?: string;
	campaign?: string;
	medium?: string;
	/** Pseudonymous PostHog distinct id, when the client has one. */
	anonId?: string | null;
}): void {
	if (!isTrackableSocialClick(input)) return;
	if (typeof window === "undefined") return;
	const campaign = input.campaign as string;
	try {
		const key = `social_click:${campaign}`;
		if (window.sessionStorage.getItem(key)) return;
		window.sessionStorage.setItem(key, "1");
		void fetch("/api/social/track", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			keepalive: true,
			body: JSON.stringify({
				campaign,
				medium: input.medium,
				...(input.anonId && input.anonId.length <= MAX_ANON_ID_LENGTH
					? { anonId: input.anonId }
					: {}),
			}),
		}).catch(() => {});
	} catch {
		// sessionStorage blocked / private mode — skip silently
	}
}
